import type { FC } from 'react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import DashboardModal from './DashboardModal'
import type { ActionButton } from './DashboardModal'
import type { ManagementDerivedKpis } from './types'
import type { CompanyAdmin, Company, Vessel, ManagementUser } from '../../core/_models'
import { showErrorToast } from '../../../../shared/utils/apiErrorMessage'

/* ─── Helpers ─── */
function downloadCsv(filename: string, headers: string[], rows: (string | number | boolean | null | undefined)[][]) {
  try {
    if (!rows || rows.length === 0) {
      toast.warn('No data available to export.')
      return
    }
    const csv = [
      headers.join(','),
      ...rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')),
    ].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)
    toast.success(`${filename} downloaded successfully.`)
  } catch (err) {
    console.error('Failed to export CSV:', err)
    showErrorToast(err, 'Failed to export CSV file.')
  }
}

/* ─── KpiDetailModal ─── */
interface KpiDetailProps {
  open: boolean
  onClose: () => void
  kpis: ManagementDerivedKpis
}

export const KpiDetailModal: FC<KpiDetailProps> = ({ open, onClose, kpis }) => {
  const items = [
    { label: 'Companies', value: kpis.totalCompanies },
    { label: 'Sub-Companies', value: kpis.totalSubCompanies },
    { label: 'Vessels', value: kpis.totalVessels },
    { label: 'Users', value: kpis.totalUsers },
    { label: 'Active Users', value: kpis.activeUsers },
    { label: 'Vendors', value: kpis.totalVendors },
    { label: 'Roles', value: kpis.totalRoles },
  ]

  const actions: ActionButton[] = [
    {
      label: 'Download Summary',
      onClick: () => downloadCsv('admin-kpi-summary.csv',
        ['Metric', 'Value'],
        items.map((i) => [i.label, String(i.value)]),
      ),
    },
  ]

  return (
    <DashboardModal open={open} onClose={onClose} title='KPI Summary' actions={actions}>
      <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
        {items.map((item, i) => (
          <div key={i} className='bg-slate-50 rounded-xl p-4 text-center'>
            <div className='text-2xl font-bold text-slate-900'>{item.value}</div>
            <div className='text-[10px] text-slate-500 mt-1'>{item.label}</div>
          </div>
        ))}
      </div>
    </DashboardModal>
  )
}

/* ─── CompaniesModal ─── */
interface CompaniesModalProps {
  open: boolean
  onClose: () => void
  companies: CompanyAdmin[]
  isFailed?: boolean
}

export const CompaniesModal: FC<CompaniesModalProps> = ({ open, onClose, companies, isFailed }) => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const q = searchQuery.toLowerCase()
  const filtered = companies.filter((c) =>
    (c.name || '').toLowerCase().includes(q) ||
    (c.email || '').toLowerCase().includes(q) ||
    (c.country || '').toLowerCase().includes(q)
  )

  const actions: ActionButton[] = [
    {
      label: 'Download CSV',
      onClick: () => downloadCsv('companies.csv',
        ['Name', 'Email', 'Contact', 'Country', 'Status'],
        filtered.map((c) => [c.name || '', c.email || '', c.contactNo || '', c.country || '', c.active ? 'Active' : 'Inactive']),
      ),
    },
    {
      label: 'View All',
      variant: 'primary',
      onClick: () => navigate('/manage/companies'),
    },
  ]

  return (
    <DashboardModal open={open} onClose={onClose} title='Companies' wide
      actions={actions}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by name, email or country...'
    >
      <div className='overflow-x-auto'>
        <table className='w-full text-[11px]'>
          <thead>
            <tr className='text-slate-400 text-[10px] uppercase tracking-wider border-b border-slate-200'>
              <th className='text-left font-semibold py-2 pr-2'>Name</th>
              <th className='text-left font-semibold py-2 px-2'>Email</th>
              <th className='text-left font-semibold py-2 px-2'>Contact</th>
              <th className='text-left font-semibold py-2 px-2'>Country</th>
              <th className='text-right font-semibold py-2 pl-2'>Status</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {isFailed ? (
              <tr>
                <td colSpan={5} className='text-center text-danger py-8 text-xs'>
                  Failed to load companies. Please close and retry from the dashboard.
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className='text-center text-slate-400 py-8 text-xs'>
                  {searchQuery ? 'No matching companies found' : 'No companies found'}
                </td>
              </tr>
            ) : (
              filtered.map((c, i) => (
                <tr key={c.id ?? i} className='hover:bg-slate-50 transition-colors'>
                  <td className='py-2.5 pr-2 font-semibold text-slate-800'>{c.name || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{c.email || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{c.contactNo || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{c.country || '—'}</td>
                  <td className='py-2.5 pl-2 text-right'>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                      c.active ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
                    }`}>
                      {c.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </DashboardModal>
  )
}

/* ─── SubCompaniesModal ─── */
interface SubCompaniesModalProps {
  open: boolean
  onClose: () => void
  subCompanies: Company[]
  isFailed?: boolean
}

export const SubCompaniesModal: FC<SubCompaniesModalProps> = ({ open, onClose, subCompanies, isFailed }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const q = searchQuery.toLowerCase()
  const filtered = subCompanies.filter((sc) =>
    (sc.name || '').toLowerCase().includes(q) ||
    (sc.email || '').toLowerCase().includes(q) ||
    (sc.uid?.username || '').toLowerCase().includes(q)
  )

  const actions: ActionButton[] = [
    {
      label: 'Download CSV',
      onClick: () => downloadCsv('sub-companies.csv',
        ['Name', 'Username', 'Email', 'Contact', 'Country', 'Status'],
        filtered.map((sc) => [sc.name || '', sc.uid?.username || '', sc.email || '', sc.contactNo || '', sc.country || '', sc.active ? 'Active' : 'Inactive']),
      ),
    },
  ]

  return (
    <DashboardModal open={open} onClose={onClose} title='Sub-Companies' wide
      actions={actions}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by name, username or email...'
    >
      <div className='overflow-x-auto'>
        <table className='w-full text-[11px]'>
          <thead>
            <tr className='text-slate-400 text-[10px] uppercase tracking-wider border-b border-slate-200'>
              <th className='text-left font-semibold py-2 pr-2'>Name</th>
              <th className='text-left font-semibold py-2 px-2'>Username</th>
              <th className='text-left font-semibold py-2 px-2'>Email</th>
              <th className='text-left font-semibold py-2 px-2'>Contact</th>
              <th className='text-left font-semibold py-2 px-2'>Country</th>
              <th className='text-right font-semibold py-2 pl-2'>Status</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {isFailed ? (
              <tr>
                <td colSpan={6} className='text-center text-danger py-8 text-xs'>
                  Failed to load sub-companies. Please close and retry from the dashboard.
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className='text-center text-slate-400 py-8 text-xs'>
                  {searchQuery ? 'No matching sub-companies found' : 'No sub-companies found'}
                </td>
              </tr>
            ) : (
              filtered.map((sc, i) => (
                <tr key={sc.id ?? i} className='hover:bg-slate-50 transition-colors'>
                  <td className='py-2.5 pr-2 font-semibold text-slate-800'>{sc.name || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{sc.uid?.username || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{sc.email || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{sc.contactNo || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{sc.country || '—'}</td>
                  <td className='py-2.5 pl-2 text-right'>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                      sc.active ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
                    }`}>
                      {sc.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </DashboardModal>
  )
}

/* ─── VesselsModal ─── */
interface VesselsModalProps {
  open: boolean
  onClose: () => void
  vessels: Vessel[]
  isFailed?: boolean
}

export const VesselsModal: FC<VesselsModalProps> = ({ open, onClose, vessels, isFailed }) => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const q = searchQuery.toLowerCase()
  const filtered = vessels.filter((v) =>
    (v.fleet_name || '').toLowerCase().includes(q) ||
    (v.imoNumber || '').toLowerCase().includes(q) ||
    (v.flag || '').toLowerCase().includes(q) ||
    (v.vesselType || '').toLowerCase().includes(q)
  )

  const actions: ActionButton[] = [
    {
      label: 'Download CSV',
      onClick: () => downloadCsv('vessels.csv',
        ['Name', 'IMO', 'Type', 'Flag', 'DWT', 'Status'],
        filtered.map((v) => [v.fleet_name || v.name || '', v.imoNumber || '', v.vesselType || '', v.flag || '', v.dwt || '', v.active ? 'Active' : 'Inactive']),
      ),
    },
    {
      label: 'View All',
      variant: 'primary',
      onClick: () => navigate('/manage/vessels'),
    },
  ]

  return (
    <DashboardModal open={open} onClose={onClose} title='Vessels' wide
      actions={actions}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by name, IMO, flag or type...'
    >
      <div className='overflow-x-auto'>
        <table className='w-full text-[11px]'>
          <thead>
            <tr className='text-slate-400 text-[10px] uppercase tracking-wider border-b border-slate-200'>
              <th className='text-left font-semibold py-2 pr-2'>Name</th>
              <th className='text-left font-semibold py-2 px-2'>IMO</th>
              <th className='text-left font-semibold py-2 px-2'>Type</th>
              <th className='text-left font-semibold py-2 px-2'>Flag</th>
              <th className='text-left font-semibold py-2 px-2'>DWT</th>
              <th className='text-right font-semibold py-2 pl-2'>Status</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {isFailed ? (
              <tr>
                <td colSpan={6} className='text-center text-danger py-8 text-xs'>
                  Failed to load vessels. Please close and retry from the dashboard.
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className='text-center text-slate-400 py-8 text-xs'>
                  {searchQuery ? 'No matching vessels found' : 'No vessels found'}
                </td>
              </tr>
            ) : (
              filtered.map((v, i) => (
                <tr key={v.id ?? i} className='hover:bg-slate-50 transition-colors'>
                  <td className='py-2.5 pr-2 font-semibold text-slate-800'>{v.fleet_name || v.name || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{v.imoNumber || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{v.vesselType || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{v.flag || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{v.dwt || '—'}</td>
                  <td className='py-2.5 pl-2 text-right'>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                      v.active ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
                    }`}>
                      {v.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </DashboardModal>
  )
}

/* ─── UsersModal ─── */
interface UsersModalProps {
  open: boolean
  onClose: () => void
  users: ManagementUser[]
  isFailed?: boolean
}

export const UsersModal: FC<UsersModalProps> = ({ open, onClose, users, isFailed }) => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const q = searchQuery.toLowerCase()
  const filtered = users.filter((u) =>
    (u.fullName || '').toLowerCase().includes(q) ||
    (u.username || '').toLowerCase().includes(q) ||
    (u.email || '').toLowerCase().includes(q) ||
    (u.roleName || '').toLowerCase().includes(q)
  )

  const actions: ActionButton[] = [
    {
      label: 'Download CSV',
      onClick: () => downloadCsv('users.csv',
        ['Name', 'Username', 'Email', 'Role', 'Company', 'Status'],
        filtered.map((u) => [u.fullName || '', u.username || '', u.email || '', u.roleName || '', u.companyName || '', u.enabled ? 'Active' : 'Disabled']),
      ),
    },
    {
      label: 'View All',
      variant: 'primary',
      onClick: () => navigate('/manage/usermanagement'),
    },
  ]

  return (
    <DashboardModal open={open} onClose={onClose} title='Users' wide
      actions={actions}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by name, username, email or role...'
    >
      <div className='overflow-x-auto'>
        <table className='w-full text-[11px]'>
          <thead>
            <tr className='text-slate-400 text-[10px] uppercase tracking-wider border-b border-slate-200'>
              <th className='text-left font-semibold py-2 pr-2'>Name</th>
              <th className='text-left font-semibold py-2 px-2'>Username</th>
              <th className='text-left font-semibold py-2 px-2'>Email</th>
              <th className='text-left font-semibold py-2 px-2'>Role</th>
              <th className='text-left font-semibold py-2 px-2'>Company</th>
              <th className='text-right font-semibold py-2 pl-2'>Status</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {isFailed ? (
              <tr>
                <td colSpan={6} className='text-center text-danger py-8 text-xs'>
                  Failed to load users. Please close and retry from the dashboard.
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className='text-center text-slate-400 py-8 text-xs'>
                  {searchQuery ? 'No matching users found' : 'No users found'}
                </td>
              </tr>
            ) : (
              filtered.map((u, i) => (
                <tr key={u.userId ?? i} className='hover:bg-slate-50 transition-colors'>
                  <td className='py-2.5 pr-2 font-semibold text-slate-800'>{u.fullName || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{u.username || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{u.email || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{u.roleName || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{u.companyName || '—'}</td>
                  <td className='py-2.5 pl-2 text-right'>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                      u.enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
                    }`}>
                      {u.enabled ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </DashboardModal>
  )
}

/* ─── VendorsModal ─── */
interface VendorsModalProps {
  open: boolean
  onClose: () => void
  vendors: any[]
  isFailed?: boolean
}

export const VendorsModal: FC<VendorsModalProps> = ({ open, onClose, vendors, isFailed }) => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const q = searchQuery.toLowerCase()
  const filtered = vendors.filter((v) =>
    (v.name || '').toLowerCase().includes(q) ||
    (v.code || '').toLowerCase().includes(q) ||
    (v.email || '').toLowerCase().includes(q) ||
    (v.companyGroupName || '').toLowerCase().includes(q)
  )

  const actions: ActionButton[] = [
    {
      label: 'Download CSV',
      onClick: () => downloadCsv('vendors.csv',
        ['Code', 'Name', 'Email', 'Contact', 'Company Group'],
        filtered.map((v) => [v.code || '', v.name || '', v.email || '', v.contactNo || '', v.companyGroupName || '']),
      ),
    },
    {
      label: 'View All',
      variant: 'primary',
      onClick: () => navigate('/manage/vendormanagement'),
    },
  ]

  return (
    <DashboardModal open={open} onClose={onClose} title='Vendors' wide
      actions={actions}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by name, code or email...'
    >
      <div className='overflow-x-auto'>
        <table className='w-full text-[11px]'>
          <thead>
            <tr className='text-slate-400 text-[10px] uppercase tracking-wider border-b border-slate-200'>
              <th className='text-left font-semibold py-2 pr-2'>Code</th>
              <th className='text-left font-semibold py-2 px-2'>Name</th>
              <th className='text-left font-semibold py-2 px-2'>Email</th>
              <th className='text-left font-semibold py-2 px-2'>Contact</th>
              <th className='text-right font-semibold py-2 pl-2'>Company Group</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {isFailed ? (
              <tr>
                <td colSpan={5} className='text-center text-danger py-8 text-xs'>
                  Failed to load vendors. Please close and retry from the dashboard.
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className='text-center text-slate-400 py-8 text-xs'>
                  {searchQuery ? 'No matching vendors found' : 'No vendors found'}
                </td>
              </tr>
            ) : (
              filtered.map((v, i) => (
                <tr key={v.id ?? i} className='hover:bg-slate-50 transition-colors'>
                  <td className='py-2.5 pr-2 font-semibold text-slate-800'>{v.code || '—'}</td>
                  <td className='py-2.5 px-2 font-semibold text-slate-800'>{v.name || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{v.email || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{v.contactNo || '—'}</td>
                  <td className='py-2.5 pl-2 text-right text-slate-500'>{v.companyGroupName || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </DashboardModal>
  )
}

/* ─── RolesModal ─── */
interface RolesModalProps {
  open: boolean
  onClose: () => void
  roles: any[]
  isFailed?: boolean
}

export const RolesModal: FC<RolesModalProps> = ({ open, onClose, roles, isFailed }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const q = searchQuery.toLowerCase()
  const filtered = roles.filter((r) =>
    (r.displayName || r.roleType || '').toLowerCase().includes(q) ||
    (r.code || '').toLowerCase().includes(q) ||
    (r.effectiveBehaviorType || '').toLowerCase().includes(q)
  )

  const actions: ActionButton[] = [
    {
      label: 'Download CSV',
      onClick: () => downloadCsv('roles.csv',
        ['Role', 'Type', 'Behavior', 'System Role', 'Description'],
        filtered.map((r) => [r.displayName || r.roleType || '', r.roleType || '', r.effectiveBehaviorType || '', r.systemRole ? 'Yes' : 'No', r.description || '']),
      ),
    },
  ]

  return (
    <DashboardModal open={open} onClose={onClose} title='Roles' wide
      actions={actions}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by name, type or behavior...'
    >
      <div className='overflow-x-auto'>
        <table className='w-full text-[11px]'>
          <thead>
            <tr className='text-slate-400 text-[10px] uppercase tracking-wider border-b border-slate-200'>
              <th className='text-left font-semibold py-2 pr-2'>Role</th>
              <th className='text-left font-semibold py-2 px-2'>Type</th>
              <th className='text-left font-semibold py-2 px-2'>Behavior</th>
              <th className='text-left font-semibold py-2 px-2'>System</th>
              <th className='text-right font-semibold py-2 pl-2'>Description</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {isFailed ? (
              <tr>
                <td colSpan={5} className='text-center text-danger py-8 text-xs'>
                  Failed to load roles. Please close and retry from the dashboard.
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className='text-center text-slate-400 py-8 text-xs'>
                  {searchQuery ? 'No matching roles found' : 'No roles found'}
                </td>
              </tr>
            ) : (
              filtered.map((r, i) => (
                <tr key={r.id ?? i} className='hover:bg-slate-50 transition-colors'>
                  <td className='py-2.5 pr-2 font-semibold text-slate-800'>{r.displayName || r.roleType || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500'>{r.roleType || '—'}</td>
                  <td className='py-2.5 px-2'>
                    <span className='inline-flex px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600'>{r.effectiveBehaviorType || '—'}</span>
                  </td>
                  <td className='py-2.5 px-2 text-slate-500'>{r.systemRole ? 'Yes' : 'No'}</td>
                  <td className='py-2.5 pl-2 text-right text-slate-500 max-w-[200px] truncate'>{r.description || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </DashboardModal>
  )
}


